import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { messages } from "../common/notification";
import { changePassword, logout } from "../redux/action/auth";
import {
  MUTED,
  cancelSx,
  dialogStyles as styles,
  inputSx,
  submitSx,
} from "../common/dialogStyles";

const EMPTY_FORM = { currentPassword: "", newPassword: "", confirmPassword: "" };

// Drives both the rule and the hint that describes it, so the two cannot drift.
const MIN_PASSWORD = 8;
const LENGTH_HINT = `At least ${MIN_PASSWORD} characters`;

// The server enforces the 8 character minimum and checks the current password;
// the rest is here so the obvious mistakes never cost a round trip.
const validate = (form) => {
  const errors = {};

  if (!form.currentPassword) errors.currentPassword = "Required";
  if (form.newPassword.length < MIN_PASSWORD) errors.newPassword = LENGTH_HINT;
  else if (form.newPassword === form.currentPassword)
    errors.newPassword = "Must differ from the current password";

  if (!form.confirmPassword) errors.confirmPassword = "Required";
  else if (form.confirmPassword !== form.newPassword)
    errors.confirmPassword = "Passwords do not match";

  return errors;
};

function ChangePasswordDialog({ open, onClose, emailAddress }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);
  const [shown, setShown] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setErrors({});
    setServerError("");
    setShown({});
  }, [open]);

  const setField = (key) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const handleSubmit = async () => {
    const found = validate(form);
    setErrors(found);
    setServerError("");

    if (Object.keys(found).length > 0) return;

    setSaving(true);
    const result = await changePassword({
      currentPassword: form.currentPassword,
      newPassword: form.newPassword,
    });
    setSaving(false);

    if (!result.success) {
      setServerError(result.message);
      return;
    }

    // The server's own wording ("Password changed, please sign in again") is
    // what the user sees, and the sign out follows it. react-hot-toast keeps
    // toasts in a global store, so it survives the redirect to the login page.
    messages.success(result.message || "Password changed");
    onClose();
    logout();
  };

  const field = (label, key, helperText) => (
    <TextField
      label={label}
      type={shown[key] ? "text" : "password"}
      value={form[key]}
      onChange={setField(key)}
      error={Boolean(errors[key])}
      helperText={errors[key] || helperText || " "}
      fullWidth
      size="small"
      sx={inputSx}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={shown[key] ? "Hide" : "Show"}>
              <IconButton
                onClick={() =>
                  setShown((current) => ({ ...current, [key]: !current[key] }))
                }
                edge="end"
                size="small"
              >
                {shown[key] ? (
                  <VisibilityOffIcon fontSize="small" />
                ) : (
                  <VisibilityIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px" } }}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !saving) handleSubmit();
      }}
    >
      <div style={styles.header}>
        <div style={styles.badge}>
          <LockResetIcon />
        </div>
        <div style={{ flex: 1 }}>
          <div style={styles.title}>Change Password</div>
          <div style={styles.subtitle}>{emailAddress}</div>
        </div>
        <IconButton onClick={onClose} size="small" sx={{ color: MUTED }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent sx={{ padding: "4px 24px 8px" }}>
        {serverError && (
          <Alert
            severity="error"
            sx={{ marginTop: "16px", borderRadius: "12px", fontFamily: "manrope" }}
          >
            {serverError}
          </Alert>
        )}

        <div style={styles.section}>
          {field("Current password", "currentPassword")}
          {field(
            "New password",
            "newPassword",
            // The hint has done its job once the length is met. The field falls
            // back to " ", so the row stays reserved and nothing shifts.
            form.newPassword.length < MIN_PASSWORD ? LENGTH_HINT : ""
          )}
          {field("Confirm new password", "confirmPassword")}
        </div>
      </DialogContent>

      <DialogActions
        sx={{ padding: "16px 24px 20px", borderTop: "1px solid #E9ECEF" }}
      >
        <Button onClick={onClose} disabled={saving} sx={cancelSx}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          disableElevation
          startIcon={
            saving ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : null
          }
          sx={submitSx}
        >
          {saving ? "Saving" : "Change password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChangePasswordDialog;
